import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Delegator } from '@evertel/schema-parser'

interface RowProps {
    id?: string
    value: any
    errors: any
    items: any
    className?: string
    documentId?: number
    schemaId?: number
    /** Field ids whose value differs from the version being compared against (Version History). */
    changedFieldIds?: Set<string>
}

const Row: React.FC<RowProps> = observer(({
    id,
    value: parentValue,
    errors: parentErrors,
    items,
    className,
    changedFieldIds,
    ...otherProps
}) => {

    const [isNarrow, setIsNarrow] = useState(false)
    const rowRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!rowRef.current) return

        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setIsNarrow(entry.contentRect.width < 400)
            }
        })

        observer.observe(rowRef.current)
        return () => observer.disconnect()
    }, [])

    if (!items?.length) return null

    // Remove overrideId from otherProps
    // this is moved over to Wrapper.tsx
    // delete otherProps.overrideId // (comes from Normalize)

    // Layout lives in _documents.scss: `.evertel-row` is a flex row with `gap` for the column
    // gutter, and consecutive rows get a top margin. Cells share the width equally via
    // `.evertel-row-cell`; when the row is narrow every cell drops to full width.
    // No padding/negative-margin utilities here - they coupled row spacing to every widget
    // forwarding className, and any widget that dropped it collapsed the gap to 0.

    return (
        <div ref={rowRef} className={classNames('evertel-row', { 'evertel-row--narrow': isNarrow })}>
            {items.map(({id: itemId, overrideId, ...itemProps}, i: number) => {
                //console.log('ROW ITEM', {itemId}, {parentValue}, {overrideId}, parentValue[overrideId], itemProps)

                // workaround for bad data in legacy schema data structure
                if (parentValue && overrideId && parentValue[overrideId]) itemId = overrideId

                const itemValue = parentValue?.[itemId]
                const itemErrors = parentErrors?.[itemId]

                return (
                    <Delegator
                        key={i}
                        {...otherProps}
                        {...itemProps}
                        id={itemId}
                        value={itemValue}
                        errors={itemErrors}
                        className="evertel-row-cell"
                        changedFieldIds={changedFieldIds}
                        isChanged={changedFieldIds?.has(itemId)}
                    />
                )
            })}
        </div>
    )
})

export { Row }
Row.displayName = 'Row (Schema)'
